export const CHECKOUT_URL =
  'https://ultrasound-visual-atlas.impultienda.ar/checkout'
export const PURCHASE_ANCHOR = '#offer'
export const META_PIXEL_ID = '1463213032525515'

export const BOX_IMAGE =
  'https://cdn.magicpatterns.com/uploads/4WsaPPPMXNEx1DuezJsyWm/Diseno_sin_titulo-8.webp'

export interface InsideImage {
  src: string
  alt: string
}

export const insideImages: InsideImage[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/wH142u13CfcMvubLgi1gRw/Captura_de_pantalla_2026-09-22_a_la(s)_7.30.51_p._m..png',
    alt: 'Abdominal ultrasound: the visual map reference page',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/sAtyHp8cUFfpR3g4HkmzJu/Captura_de_pantalla_2026-09-22_a_la(s)_7.31.48_p._m..png',
    alt: 'Liver: gross anatomy and ultrasound orientation reference page',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/816Lu3SkTCwjmmToKnSN6Z/Captura_de_pantalla_2026-09-22_a_la(s)_7.32.09_p._m..png',
    alt: 'Gallbladder sludge vs stone-like appearance comparison page',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/q8yC2ZSrdu2dBDMBF1Vpfk/Captura_de_pantalla_2026-09-22_a_la(s)_7.32.31_p._m..png',
    alt: 'Pancreas: anatomy and key landmarks reference page',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/q2AX5Z78eHZ2F6FtxyXGb5/Captura_de_pantalla_2026-09-22_a_la(s)_7.33.05_p._m..png',
    alt: 'Aorta and inferior vena cava: abdominal landmarks reference page',
  },
]

export const systemBullets = [
  'Probe orientation & ultrasound fundamentals',
  'Abdominal ultrasound anatomy',
  'Renal, urinary & pelvic ultrasound',
  'Obstetric & gynecologic ultrasound',
  'Vascular, thyroid & soft-tissue ultrasound',
  'Common findings & pattern recognition',
]

export type ModuleColor = 'navy' | 'sono' | 'emerald' | 'purple' | 'teal' | 'charcoal'

export interface Module {
  number: number
  title: string
  body: string
  steps: string
  color: ModuleColor
}

export const modules: Module[] = [
  {
    number: 1,
    title: 'ULTRASOUND FOUNDATIONS & IMAGE ORIENTATION',
    body: 'Build a clearer foundation for understanding how ultrasound images are created and oriented. Learn transducers, probe positioning, scanning planes, echogenicity, gain, depth, focus and common ultrasound artifacts through organized visual references.',
    steps: 'Orient. Adjust. Understand.',
    color: 'navy',
  },
  {
    number: 2,
    title: 'ABDOMINAL ULTRASOUND VISUAL ATLAS',
    body: 'Understand abdominal ultrasound anatomy and recognize important sonographic patterns more systematically. Explore the liver, gallbladder, biliary system, pancreas, spleen and major abdominal landmarks through scanning windows, annotated anatomy and normal-versus-abnormal comparisons.',
    steps: 'Locate. Compare. Recognize.',
    color: 'sono',
  },
  {
    number: 3,
    title: 'RENAL, URINARY & PELVIC ULTRASOUND ATLAS',
    body: 'Learn to recognize kidneys, bladder and pelvic structures across common ultrasound planes. Explore renal anatomy, collecting-system patterns, stones, cystic appearances, bladder orientation and essential pelvic landmarks through structured visual references.',
    steps: 'Scan. Identify. Compare.',
    color: 'emerald',
  },
  {
    number: 4,
    title: 'OBSTETRIC & GYNECOLOGIC ULTRASOUND ATLAS',
    body: 'Study essential female pelvic and pregnancy ultrasound references through organized visual comparisons. Explore uterine and ovarian anatomy, common structural patterns, gestational orientation, fetal anatomy, placenta and key sonographic landmarks.',
    steps: 'Orient. Observe. Recognize.',
    color: 'purple',
  },
  {
    number: 5,
    title: 'VASCULAR, THYROID & SOFT-TISSUE ULTRASOUND ATLAS',
    body: 'Expand your ultrasound study beyond abdominal and pelvic imaging. Explore vascular orientation, basic Doppler patterns, thyroid anatomy, superficial structures, muscles, tendons and soft-tissue appearances through practical visual references.',
    steps: 'Locate. Compare. Understand.',
    color: 'teal',
  },
  {
    number: 6,
    title: 'COMMON ULTRASOUND FINDINGS & PATTERN RECOGNITION',
    body: 'Bring anatomy, scanning orientation and sonographic appearances together through structured comparison. Study normal-versus-abnormal patterns, cystic and solid appearances, calcifications, shadowing, enhancement, collections, echogenicity changes and guided recognition examples.',
    steps: 'Observe. Compare. Recognize.',
    color: 'charcoal',
  },
]

export interface Bonus {
  number: number
  title: string
  subtitle: string
  body: string
  steps: string
}

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'ULTRASOUND PROBE POSITIONING & SCANNING WINDOWS GUIDE™',
    subtitle: 'Know Where to Position the Probe and What to Expect',
    body: 'Connect probe placement, marker direction, scanning plane and expected screen anatomy through organized positioning diagrams and scanning-window references.',
    steps: 'Position. Orient. Scan.',
  },
  {
    number: 2,
    title: 'NORMAL ULTRASOUND ANATOMY QUICK REFERENCE™',
    subtitle: 'Essential Sonographic Anatomy in One Organized Reference',
    body: 'Quickly review normal ultrasound anatomy, expected echogenicity, anatomical landmarks and common scanning planes across essential body regions.',
    steps: 'Locate. Identify. Review.',
  },
  {
    number: 3,
    title: 'ULTRASOUND ARTIFACTS RECOGNITION GUIDE™',
    subtitle: 'Understand What the Image Is Really Showing You',
    body: 'Recognize shadowing, enhancement, reverberation, mirror image, anisotropy and other common artifacts while understanding why they appear and how technique affects them.',
    steps: 'Recognize. Verify. Understand.',
  },
  {
    number: 4,
    title: 'ULTRASOUND IMAGE OPTIMIZATION QUICK GUIDE™',
    subtitle: 'Understand the Controls Behind a Clearer Ultrasound Image',
    body: 'Use practical references for depth, gain, focus, frequency, TGC, dynamic range and Doppler controls to understand how technical adjustments change the displayed image.',
    steps: 'Adjust. Optimize. Review.',
  },
  {
    number: 5,
    title: 'ULTRASOUND FINDINGS COMPARISON & DIFFERENTIATION GUIDE™',
    subtitle: 'Compare Similar Sonographic Patterns More Systematically',
    body: 'Compare cystic, solid, complex, echogenic, shadowing and other ultrasound appearances using structured observation frameworks and side-by-side references.',
    steps: 'Describe. Compare. Differentiate.',
  },
]

export interface Differentiator {
  icon: string
  title: string
  body: string
}

export const differentiators: Differentiator[] = [
  {
    icon: '🩻',
    title: 'Practical visual references',
    body: 'Find the anatomy, scanning window or ultrasound concept you need when studying.',
  },
  {
    icon: '🔍',
    title: 'Structured learning system',
    body: 'Move from probe orientation and normal anatomy to comparison and pattern recognition.',
  },
  {
    icon: '⚡',
    title: '6 ultrasound study areas',
    body: 'Fundamentals, abdomen, renal and pelvic, OB/GYN, vascular and soft tissue, plus integrated findings.',
  },
  {
    icon: '🔄',
    title: 'Ready to use again and again',
    body: 'Return to the atlas whenever you need to review an ultrasound concept.',
  },
]

export const offerBullets = [
  '6 comprehensive modules',
  '5 strategic bonuses',
  'Probe positioning & image orientation',
  'Abdominal ultrasound anatomy',
  'Renal, pelvic & OB/GYN references',
  'Vascular, thyroid & soft-tissue ultrasound',
  'Pattern recognition & comparison tools',
]

export interface Testimonial {
  name: string
  role: string
  location: string
  quote: string
}

export const testimonials: Testimonial[] = [
  {
    name: 'Hannah Whitfield, 23',
    role: 'Diagnostic medical sonography student, 2nd year',
    location: 'Birmingham, United Kingdom',
    quote:
      'My biggest problem was connecting what my hand was doing with what appeared on the screen. The probe positioning guide and the scanning-window pages fixed that. Before every lab session I review the region, the marker direction and the expected anatomy — and my clinical instructor noticed I stopped getting lost when I changed planes.',
  },
  {
    name: 'Marcus Bennett, 26',
    role: 'Sonography student, clinical placement',
    location: 'Houston, United States',
    quote:
      'Artifacts used to confuse me completely. Shadowing, enhancement, reverberation, mirror image — I knew the definitions but could not recognize them on a real image. The artifacts guide shows each one visually and explains why it happens. Now I actually use artifacts to understand what I am looking at instead of ignoring them.',
  },
  {
    name: 'Emily Carter, 24',
    role: 'Medical student, emergency medicine rotation',
    location: 'Ottawa, Canada',
    quote:
      'The abdominal atlas is what I open most. Liver, gallbladder, pancreas, aorta and IVC with the anatomy illustration right beside the ultrasound image — it finally made cross-sectional orientation click. I felt much more confident during bedside ultrasound teaching on my rotation.',
  },
  {
    name: 'Olivia Nguyen, 22',
    role: 'Sonography student, 1st year',
    location: 'Sydney, Australia',
    quote:
      'I study OB/GYN on the train, so I needed something organized I could open anywhere. The obstetric and gynecologic module lays out uterine, ovarian and fetal landmarks in a way that is easy to review in twenty minutes. My exam revision became so much less stressful.',
  },
  {
    name: 'Jessica Morales, 28',
    role: 'Radiologic technologist transitioning to ultrasound',
    location: 'Phoenix, United States',
    quote:
      'Coming from X-ray, ultrasound felt like a completely different language. Echogenicity, gain, depth, TGC — the image optimization guide explained every control with before-and-after visuals. After a month I could adjust the machine and understand why the image improved, instead of turning knobs randomly.',
  },
]

export interface Faq {
  q: string
  a: string
}

export const faqs: Faq[] = [
  {
    q: 'Who is this system for?',
    a: 'Ultrasound students, sonography students, medical students and healthcare learners studying diagnostic ultrasound.',
  },
  {
    q: 'Is this a physical product?',
    a: 'No. ULTRASOUND VISUAL ATLAS™ is a 100% digital resource.',
  },
  {
    q: 'Is this a training course or certification?',
    a: 'No. It is an educational visual study and reference system.',
  },
  {
    q: 'Is it another theory-heavy ebook?',
    a: 'No. It focuses on annotated ultrasound references, probe orientation, anatomy, comparisons, artifacts and structured pattern-recognition tools.',
  },
  {
    q: 'What areas are included?',
    a: 'Ultrasound fundamentals, abdomen, renal and pelvic imaging, OB/GYN, vascular, thyroid and soft-tissue ultrasound, plus pattern-recognition references.',
  },
  {
    q: 'Does this replace formal ultrasound training?',
    a: 'No. It is an educational study resource and does not replace formal training, supervised scanning, clinical protocols or professional medical judgment.',
  },
  {
    q: 'How will I receive it?',
    a: "You'll receive immediate digital access after purchase.",
  },
  {
    q: 'What devices can I use it on?',
    a: 'Any device that opens PDF files — laptop, tablet, phone or desktop. You can also print the reference pages you use most in the lab.',
  },
  {
    q: 'Do I need previous scanning experience?',
    a: 'No. The system starts with probe orientation, scanning planes and normal anatomy, then moves into comparisons and pattern recognition, so early students and more advanced learners can both use it.',
  },
  {
    q: 'How long do I keep access, and what if it is not for me?',
    a: 'Access is yours to keep and review whenever you need it. If it is not right for you, contact us within 7 days for a full refund according to the refund terms.',
  },
]

export interface PopupSale {
  name: string
  city: string
  country: string
}

export const popupSales: PopupSale[] = [
  { name: 'Emily W.', city: 'Manchester', country: 'United Kingdom' },
  { name: 'James C.', city: 'Toronto', country: 'Canada' },
  { name: 'Sophia M.', city: 'Austin', country: 'United States' },
  { name: 'Oliver B.', city: 'Melbourne', country: 'Australia' },
  { name: 'Grace T.', city: 'Leeds', country: 'United Kingdom' },
  { name: 'Ethan R.', city: 'Vancouver', country: 'Canada' },
  { name: 'Ava S.', city: 'Chicago', country: 'United States' },
  { name: 'Liam H.', city: 'Brisbane', country: 'Australia' },
  { name: 'Charlotte D.', city: 'Bristol', country: 'United Kingdom' },
  { name: 'Noah P.', city: 'Calgary', country: 'Canada' },
  { name: 'Isabella R.', city: 'Miami', country: 'United States' },
  { name: 'Jack M.', city: 'Perth', country: 'Australia' },
]
